import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { createPaymentIntent } from '../services/paymentsService';
import { releaseBoothRemote, setPendingPaymentRemote } from '../services/reservationsService';
import { createTransaction, addFailureAttempt } from '../services/transactionsService';
import { updateCartItemAction, loadActiveCartItems, loadCartItemsByStatus } from '../services/cartItemsService';
import { getEvent } from '../services/eventsService';
import {getFunctionConfig} from '../services/defaultsService';
import './ProfilePage.css';

const normalizeConfiguredValue = (value) => {
  const normalized = String(value || '').trim();
  return normalized.toLowerCase() === 'na' ? '' : normalized;
};

// Stripe will be initialized inside the component using function config
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatUsdFromCents = (cents) => usdFormatter.format(Number(cents || 0) / 100);
const PAYMENT_SUCCESS_CONTEXT_KEY = 'paymentSuccessContext';

// Checkout Form Component
  const CheckoutForm = ({ paymentIntentId, amount, onSuccess, onError, onCancel, customerEmail, customerPhone, customerZip, customerCountry, customerState, customerCity, customerAddressLine, onPaymentStart, onPaymentComplete, policyAcknowledged }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');

  // Watch for PaymentElement failing to mount (e.g. invalid publishable key)
  useEffect(() => {
    if (!stripe || !elements) return;
    let mounted = false;
    const check = () => {
      try {
        const paymentEl = elements.getElement(PaymentElement);
        if (paymentEl) mounted = true;
      } catch (e) {
        // ignore
      }
    };
    // Check immediately and again after a short delay
    check();
    const timeout = setTimeout(() => {
      check();
      if (!mounted) {
        const err = { type: 'payment_element_load_error', message: 'Payment form failed to load. Possible invalid publishable key or network error.' };
        console.error('[Checkout] PaymentElement load error detected', err);
        setMessage('Payment form failed to load. See console for details.');
        if (typeof onError === 'function') onError(err);
        try {
          window.dispatchEvent(new CustomEvent('stripe-payment-element-load-error', { detail: err }));
        } catch (e) {
          // ignore
        }
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, [stripe, elements, onError]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage('');
    
    // Notify parent that payment processing has started (pause timer)
    const paymentStartTime = Date.now();
    if (typeof onPaymentStart === 'function') {
      onPaymentStart();
    }

    try {
      // Ensure the PaymentElement has actually mounted — stripe.confirmPayment
      // requires a mounted Payment Element (or Express Checkout element).
      const paymentEl = elements.getElement(PaymentElement);
      if (!paymentEl) {
        setIsProcessing(false);
        setMessage('Payment form not ready. Please wait a moment and try again.');
        if (typeof onPaymentComplete === 'function') {
          onPaymentComplete(false, 0);
        }
        return;
      }

      // Validate the PaymentElement before submitting
      // This will catch incomplete fields (e.g., incomplete card number) before Stripe processes
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setIsProcessing(false);
        setMessage(submitError.message || 'Please check all fields in the payment form and correct any errors.');
        if (typeof onPaymentComplete === 'function') {
          onPaymentComplete(false, 0);
        }
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              email: customerEmail || 'noreply@icnacon.org',
              phone: customerPhone || '+10000000000',
                address: {
                country: customerCountry || 'US',
                state: customerState || '',
                // Payment Element is configured to NOT collect some address fields in the element,
                // so pass them here. Stripe requires these when fields are opt-out.
                line1: customerAddressLine || '',
                city: customerCity || '',
                // Postal code may be collected by the element (postalCode: 'auto'),
                // but including it here ensures confirmPayment has a value when element
                // is configured to omit address fields.
                postal_code: customerZip || undefined,
              }
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        // Calculate how long the payment attempt took
        const paymentDuration = Math.floor((Date.now() - paymentStartTime) / 1000);
        
        setMessage(error.message);
        onError(error);
        
        // Notify parent that payment failed (resume timer with extension)
        if (typeof onPaymentComplete === 'function') {
          onPaymentComplete(false, paymentDuration);
        }
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
        
        // Notify parent that payment succeeded
        if (typeof onPaymentComplete === 'function') {
          onPaymentComplete(true, 0);
        }
      } else {
        setMessage('Payment processing. Status: ' + paymentIntent?.status);
      }
    } catch (err) {
      // Calculate how long the payment attempt took
      const paymentDuration = Math.floor((Date.now() - paymentStartTime) / 1000);
      
      setMessage(err.message || 'An error occurred');
      onError(err);
      
      // Notify parent that payment failed (resume timer with extension)
      if (typeof onPaymentComplete === 'function') {
        onPaymentComplete(false, paymentDuration);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Whether the PaymentElement has mounted (used to disable submit until ready)
  const paymentElementMounted = !!(elements && elements.getElement && elements.getElement(PaymentElement));

  // PaymentElement options - use metadata for email and phone, only collect zip code
  const paymentElementOptions = {
    fields: {
      billingDetails: {
        email: 'never', // We'll use email from metadata
        phone: 'never', // We'll use phone from metadata
        address: {
          country: 'never',
          line1: 'never',
          line2: 'never',
          city: 'never',
          state: 'never',
          postalCode: 'auto', // Collect zip/postal code only
        },
      },
    },
    wallets: {
      applePay: 'auto',
      googlePay: 'auto',
    },
    layout: 'tabs',
  };

  // Prefill postal code in the PaymentElement when available
  const paymentElementDefaultValues = {
    billingDetails: {
      address: {
        postal_code: customerZip || undefined,
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
      <PaymentElement options={paymentElementOptions} defaultValues={paymentElementDefaultValues} />
      
      {!policyAcknowledged && (
        <div 
          style={{ 
            marginTop: 16,
            padding: 12,
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 4,
            color: '#856404',
            fontSize: '0.95em'
          }}
        >
          ⚠️ Please scroll down and check the acknowledgment checkbox to proceed with payment.
        </div>
      )}
      
      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          type="submit"
          disabled={!stripe || isProcessing || !policyAcknowledged || !paymentElementMounted}
          className="back-btn"
          style={{ 
            minWidth: 180, 
            backgroundColor: '#1976d2', 
            color: '#fff',
            opacity: (!stripe || isProcessing || !policyAcknowledged || !paymentElementMounted) ? 0.6 : 1,
          }}
          title={!policyAcknowledged ? 'Please acknowledge the policies below to proceed' : ''}
        >
          {isProcessing ? 'Processing...' : `Pay ${formatUsdFromCents(amount)}`}
        </button>
        
        <button
          type="button"
          onClick={onCancel}
          className="cancel-btn"
          disabled={isProcessing}
        >
          Cancel Checkout
        </button>
      </div>

      {message && (
        <div 
          className={message.includes('success') ? 'profile-success' : 'profile-error'}
          style={{ marginTop: 16 }}
        >
          {message}
        </div>
      )}
    </form>
  );
};

// Main Checkout Page Component
const StripeCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  // Stripe promise will be initialized from the event's bazaarPaymentInfo.clientId
  const [stripePromise, setStripePromise] = useState(null);

  // NOTE: we intentionally do NOT load a default Stripe key from config/environment.
  // The publishable key must be provided on the event as `bazaarPaymentInfo.clientId`.
  
  // Get checkout data from navigation state
  const checkoutData = location.state || {};
  const {
    amount = 0,
    description = 'Purchase',
    metadata = {},
    eventName = '',
    booths = [],
    paymentIntentId: existingPaymentIntentId = '',
    clientSecret: existingClientSecret = '',
    orderData = null,
    paymentWaitMinutes,
    paymentStartTime = Date.now(), // Start time passed from shopping cart
    eventId = null,
    layoutId = null,
  } = checkoutData;

  // Prefer eventId from navigation root, but fall back to orderData or metadata
  const resolvedEventId = orderData?.eventId || eventId || metadata?.eventId || null;
  const purchaseType = orderData?.metadata?.purchaseType || metadata?.purchaseType || null;
  const parsedPaymentWaitMinutes = Number.parseInt(paymentWaitMinutes, 10);
  const hasValidPaymentWait = Number.isFinite(parsedPaymentWaitMinutes) && parsedPaymentWaitMinutes > 0;

  const [clientSecret, setClientSecret] = useState(existingClientSecret);
  const [paymentIntentId, setPaymentIntentId] = useState(existingPaymentIntentId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Event data for policies
  const [eventData, setEventData] = useState(null);
  // Default to true so checkout isn't blocked when no event policies are present.
  // If event data contains policies we will flip this to false (require ack).
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  
  // Payment timeout state
  const [paymentTimeLeft, setPaymentTimeLeft] = useState(hasValidPaymentWait ? (parsedPaymentWaitMinutes * 60) : null); // seconds
  const [timeoutExpired, setTimeoutExpired] = useState(false);
  const timeoutReleaseInProgress = useRef(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const pausedTimeRef = useRef(null); // Track when timer was paused
  const [attemptCount, setAttemptCount] = useState(0); // Track attempt count (synced with database)
  const MAX_PAYMENT_ATTEMPTS = 3;
  const timerResetTime = useRef(paymentStartTime); // Track when timer was last reset
  const paymentIntentCreated = useRef(false); // Track if payment intent has been created
  const paymentPendingMarked = useRef(false); // Prevent duplicate setpendingpayment calls
  // Idempotency token for setpendingpayment — generated once per checkout session
  const checkoutLockId = useRef(
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `lock-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    if (purchaseType !== 'booths') return;
    if (hasValidPaymentWait) return;
    setError('Missing or invalid payment timer configuration for this event. Please contact support.');
  }, [purchaseType, hasValidPaymentWait]);

  // Get the display values early so they can be used in effects
  const displayAmount = orderData?.amount || amount;
  const displayEventName = orderData?.eventName || eventName;
  const displayBooths = orderData?.booths || booths;
  const displayDescription = orderData?.description || description;
  const customerEmail = orderData?.metadata?.payeeEmail || metadata?.payeeEmail || null;
  const customerPhone = orderData?.metadata?.payeePhone || metadata?.payeePhone || null;
  const customerZip = orderData?.metadata?.payeeZip || metadata?.payeeZip || null;
  const customerCountry = orderData?.metadata?.payeeCountry || metadata?.payeeCountry || 'US';
  const customerState = orderData?.metadata?.payeeState || metadata?.payeeState || '';
  const customerCity = orderData?.metadata?.payeeCity || metadata?.payeeCity || '';
  const customerAddressLine = orderData?.metadata?.payeeStreet || metadata?.payeeStreet || '';

  const persistPaymentSuccessContext = (currentPaymentIntentId) => {
    if (!currentPaymentIntentId) return;

    const context = {
      paymentIntentId: currentPaymentIntentId,
      amount: displayAmount || 0,
      eventName: displayEventName || '',
      booths: Array.isArray(displayBooths) ? displayBooths : [],
      metadata: {
        ...(orderData?.metadata || metadata || {}),
        eventId: eventId || orderData?.eventId || metadata?.eventId || '',
        layoutId: layoutId || orderData?.layoutId || metadata?.layoutId || '',
      },
      eventId: eventId || orderData?.eventId || metadata?.eventId || '',
      layoutId: layoutId || orderData?.layoutId || metadata?.layoutId || '',
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(`${PAYMENT_SUCCESS_CONTEXT_KEY}:${currentPaymentIntentId}`, JSON.stringify(context));
      sessionStorage.setItem(`${PAYMENT_SUCCESS_CONTEXT_KEY}:last`, JSON.stringify(context));
    } catch (storageErr) {
      console.warn('[StripeCheckout] Unable to persist payment success context:', storageErr?.message || storageErr);
    }
  };

  const markBoothsPendingForPayment = async (currentPaymentIntentId) => {
    if (paymentPendingMarked.current) return;
    if (purchaseType !== 'booths') return;
    if (!Array.isArray(displayBooths) || displayBooths.length === 0) return;

    const currentVendorId = (orderData?.metadata || metadata)?.vendorId;
    const currentSessionKey = `cartSession:${currentUser?.uid}:${eventId}:${layoutId}`;
    const currentSessionId = sessionStorage.getItem(currentSessionKey);
    if (!currentVendorId || !currentSessionId || !currentPaymentIntentId) return;

    paymentPendingMarked.current = true;
    try {
      for (const booth of displayBooths) {
        const boothId = typeof booth === 'object' ? (booth?.id || booth?.boothId) : booth;
        const boothNumber = typeof booth === 'object' ? (booth?.number || booth?.boothNumber || null) : null;
        if (!boothId) continue;
        await setPendingPaymentRemote({
          eventId,
          layoutId,
          boothId,
          boothNumber,
          vendorId: currentVendorId,
          createdByProfileId: currentUser?.uid,
          sessionId: currentSessionId,
          paymentIntentId: currentPaymentIntentId,
          checkoutLockId: checkoutLockId.current,
          paymentWaitMinutes: parsedPaymentWaitMinutes,
        });
      }
    } catch (payErr) {
      paymentPendingMarked.current = false;
      // Non-fatal: log but do not block checkout. Backend will still honour
      // the cart hold until expiresAt; the cart timer race risk remains but
      // payment can still succeed.
      console.warn('[StripeCheckout] setPendingPaymentRemote failed (non-fatal):', payErr?.message || payErr);
    }
  };

  // Fetch event data for policies
  useEffect(() => {
    if (!resolvedEventId) return;
    let cancelled = false;
    (async () => {
      try {
        const event = await getEvent(resolvedEventId);
        if (!cancelled) {
          setEventData(event);
          const hasPolicies = event && (event.cancellationPolicy || event.termsAndConditions || event.policyLink);
          setPolicyAcknowledged(false);
          try {
            const eventKey = normalizeConfiguredValue(event?.bazaarPaymentInfo?.clientId);
            if (eventKey && typeof eventKey === 'string' && eventKey.trim()) {
              setStripePromise(loadStripe(eventKey.trim()));
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (err) {
        console.error('[StripeCheckout] Failed to load event data:', err);
      }
    })();
    // Listen for PaymentElement load errors dispatched by the child form
    const handleExternalLoadError = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : ev;
        console.error('[StripeCheckout] PaymentElement load error detail:', detail);
        const friendly = 'Due to a system issue we cannot load the payment form. Please contact the event admin.';
        setError(friendly);
        setLoading(false);
      } catch (e) {
        console.error('[StripeCheckout] Error handling external load error:', e);
        setError('Due to a system issue we cannot load the payment form. Please contact the event admin.');
        setLoading(false);
      }
    };
    window.addEventListener('stripe-payment-element-load-error', handleExternalLoadError);
    return () => { cancelled = true; };
  }, [resolvedEventId]);


  // Create payment intent on component mount (only if not already provided)
  useEffect(() => {
    // Prevent multiple executions - check AND set in the same conditional
      if (paymentIntentCreated.current) {
        return;
    }
    
    // Set the flag immediately to prevent concurrent execution
    paymentIntentCreated.current = true;
    
    // If we already have a payment intent, use it
    if (existingClientSecret && existingPaymentIntentId) {
      setClientSecret(existingClientSecret);
      setPaymentIntentId(existingPaymentIntentId);
      persistPaymentSuccessContext(existingPaymentIntentId);
      void markBoothsPendingForPayment(existingPaymentIntentId);
      setLoading(false);
      return;
    }

    // Otherwise, create a new payment intent
    let effectiveAmount = orderData?.amount || amount;

    if (!effectiveAmount || effectiveAmount <= 0) {
      setError('Invalid amount. Please go back and try again.');
      setLoading(false);
      return;
    }

    if (purchaseType === 'booths' && !hasValidPaymentWait) {
      setError('Missing or invalid payment timer configuration for this event. Please contact support.');
      setLoading(false);
      return;
    }

    const createIntent = async () => {
      try {
        setLoading(true);

        const paymentMethodConfiguration = normalizeConfiguredValue(eventData?.bazaarPaymentInfo?.paymentConfigId);
        
        const result = await createPaymentIntent({
          amount: effectiveAmount, // Always in cents
          currency: orderData?.currency || 'usd',
          description: orderData?.description || description,
          metadata: orderData?.metadata || metadata,
          eventId: resolvedEventId || eventId || (orderData?.metadata && orderData.metadata.eventId) || '',
          payment_method_types: ['card', 'amazon_pay'],
          payment_method_configuration: paymentMethodConfiguration,
        });

        setClientSecret(result.clientSecret);
        setPaymentIntentId(result.paymentIntentId);
        persistPaymentSuccessContext(result.paymentIntentId);
        await markBoothsPendingForPayment(result.paymentIntentId);

        // Create initial transaction record with 'incomplete' status
        // This will be updated to 'succeeded' or 'failed' when payment completes
        // NOTE: Only create if transaction doesn't already exist (usually created in BoothShoppingCart)
        if (currentUser?.uid) {
          try {
            // Check if transaction already exists
            const { getTransaction } = await import('../services/transactionsService');
            
            const existingTx = await getTransaction(result.paymentIntentId);
            
            if (!existingTx) {
              
              
              // Resolve eventId for logging and persistence
              const _resolvedEventId = (orderData?.eventId || eventId) || (orderData?.metadata?.eventId || metadata?.eventId) || '';
              if (!_resolvedEventId) {
                try {
                  // One-time debug: log when eventId could not be resolved for this createTransaction
                  // eslint-disable-next-line no-console
                  console.warn('[StripeCheckout] createTransaction: no eventId resolved for paymentIntentId', result.paymentIntentId, 'metadata:', orderData?.metadata || metadata);
                } catch (e) {}
              }

              await createTransaction({
                paymentIntentId: result.paymentIntentId,
                profileId: currentUser.uid,
                eventId: _resolvedEventId,
                layoutId: (orderData?.layoutId || layoutId) || null,
                amount: effectiveAmount,
                currency: result.currency || 'usd',
                status: 'incomplete',
                metadata: orderData?.metadata || metadata,
                booths: Array.isArray(booths) ? booths.map(b => ({
                  id: typeof b === 'object' ? (b.id || '') : '',
                  number: typeof b === 'object' ? (b.number || b) : b,
                  level: typeof b === 'object' ? (b.level || '') : '',
                  corner: typeof b === 'object' ? (b.corner || '') : '',
                  row: typeof b === 'object' ? (b.row || 0) : 0,
                  col: typeof b === 'object' ? (b.col || 0) : 0,
                  price: typeof b === 'object' ? Math.round((b.price || 0) * 100) : 0, // Convert dollars to cents
                })) : [],
              });
              
            } else {
              
            }
          } catch (txError) {
            console.error('[Checkout] ❌ Failed to create/check initial transaction record:', txError);
            console.error('[Checkout] Error details:', txError.message, txError.code);
            // Don't block checkout if transaction logging fails
          }
        } else {
          console.warn('[Checkout] No currentUser.uid, skipping transaction creation');
        }
      } catch (err) {
        console.error('[Checkout] Failed to create payment intent:', err);
        setError(err.message || 'Failed to initialize payment');
      } finally {
        setLoading(false);
      }
    };

    createIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingClientSecret, existingPaymentIntentId, amount, orderData, purchaseType, hasValidPaymentWait]);

  // Clear cart items when entering checkout (one-way trip - no going back)
  const cartClearedRef = useRef(false);
  
  useEffect(() => {
    if (purchaseType !== 'booths' || !currentUser?.uid || !eventId || !layoutId) return;
    if (cartClearedRef.current) return; // Prevent duplicate execution
    
    // Mark that we've started clearing IMMEDIATELY to prevent duplicate execution
    cartClearedRef.current = true;
    
    const clearCart = async () => {
      try {
        const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
        const sessionId = sessionStorage.getItem(sessionKey);
        
        if (!sessionId) {
          
          return;
        }
        
        
        
        // Load all active cart items and mark them as 'checkedout' to clear from cart
        const activeCartItems = await loadActiveCartItems({
          profileId: currentUser.uid,
          eventId,
          layoutId,
          sessionId
        });
        
        
        
        if (activeCartItems.length === 0) {
          return;
        }
        
        for (const cartItem of activeCartItems) {
          try {
            await updateCartItemAction(cartItem.cartItemId || cartItem.id, 'checkedout', {
              triggeredByProfileIdOverride: currentUser.uid,
              triggeredByOverride: 'checkout-started'
            });
          } catch (err) {
            console.error('[StripeCheckout] Failed to clear cart item:', cartItem.cartItemId || cartItem.id, err);
          }
        }
      } catch (err) {
        console.error('[StripeCheckout] Error clearing cart:', err);
      }
    };
    
    clearCart();
  }, [currentUser?.uid, eventId, layoutId, purchaseType]);

  // Payment timeout timer
  useEffect(() => {
    if (purchaseType !== 'booths') return;
    if (!hasValidPaymentWait) return;
    if (timeoutExpired) return;
    if (timerPaused) return; // Don't tick when paused

    let cancelled = false;
    const tick = () => {
      if (cancelled || timerPaused) return;
      // Use timerResetTime instead of paymentStartTime to allow for resets
      const elapsed = Math.floor((Date.now() - timerResetTime.current) / 1000);
      const remaining = Math.max(0, parsedPaymentWaitMinutes * 60 - elapsed);
      setPaymentTimeLeft(remaining);

      if (remaining <= 0 && !timeoutReleaseInProgress.current) {
        timeoutReleaseInProgress.current = true;
        setTimeoutExpired(true);
        
        // Payment timeout reached - don't release booths immediately
        // Instead, enter a grace period where webhook or fallback can still finalize
        // Booths will expire naturally on server-side after grace window (e.g., +5 min)
        console.log('[Checkout] Payment timeout reached. Entering grace period - waiting for webhook/fallback finalization.');
        
        // DO NOT release booths here - let grace period handler and reconciler manage cleanup
        // This prevents the race condition where booth is released before late webhook arrives
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [parsedPaymentWaitMinutes, hasValidPaymentWait, timeoutExpired, timerPaused, currentUser?.uid, eventId, layoutId, displayBooths, purchaseType]);

  const handlePaymentSuccess = (paymentIntent) => {
    persistPaymentSuccessContext(paymentIntent.id);

    // Log resolved values to help trace nulls in navigation state
    console.debug('[StripeCheckout] navigating to payment-success with:', {
      paymentIntentId: paymentIntent.id,
      amount: paymentIntent.amount,
      eventName: displayEventName,
      booths: displayBooths,
      metadata: orderData?.metadata || metadata,
      eventId,
      layoutId,
    });

    // Navigate to success page with payment details (use computed display values)
    navigate('/payment-success', {
      state: {
        paymentIntentId: paymentIntent.id,
        amount: paymentIntent.amount,
        eventName: displayEventName || 'ICNA Event',
        booths: displayBooths || [],
        metadata: {
          ...(orderData?.metadata || metadata),
          eventId: eventId, // Add eventId explicitly
          layoutId: layoutId, // Add layoutId explicitly
        },
        // Also pass them separately for easier access
        eventId: eventId,
        layoutId: layoutId,
      },
    });
  };

  const handlePaymentStart = () => {
    pausedTimeRef.current = paymentTimeLeft; // Store current time left
    setTimerPaused(true);
  };

  const handlePaymentComplete = (success, durationSeconds) => {
    if (success) {
      // Payment succeeded - timer doesn't matter anymore
      setTimerPaused(false);
    } else {
      // Payment failed - increment attempt count (will be synced with database by handlePaymentError)
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);
      
      if (newAttemptCount <= MAX_PAYMENT_ATTEMPTS) {
        // Reset timer to full paymentWait time (give them a fresh start)
        // Reset the timer reference point to NOW
        timerResetTime.current = Date.now();
        setPaymentTimeLeft(hasValidPaymentWait ? (parsedPaymentWaitMinutes * 60) : null);
        setTimerPaused(false);
        pausedTimeRef.current = null;
      } else {
        // Exceeded maximum attempts - let timer continue from where it was
        const newTimeLeft = pausedTimeRef.current || paymentTimeLeft;
        setPaymentTimeLeft(newTimeLeft);
        setTimerPaused(false);
        pausedTimeRef.current = null;
      }
    }
  };

  const handlePaymentError = async (error) => {
    // Surface error to UI so mount/load failures are visible

    try {
      console.error('[StripeCheckout] payment error:', error);
      const friendly = 'Due to a system issue we cannot process your payment right now. Please contact the event admin.';
      setError(friendly);
    } catch (e) {
      console.error('[StripeCheckout] Error while setting payment error:', e);
      setError('Due to a system issue we cannot process your payment right now. Please contact the event admin.');
    }

    // Log declined/failed payment attempts by appending to existing transaction
    if (currentUser?.uid && paymentIntentId) {
      try {
        const failureReason = error?.message || error?.code || 'Payment declined or failed';
        const errorDetails = error?.code ? ` (${error.code})` : '';
        const fullReason = `${failureReason}${errorDetails}`;
        
        await addFailureAttempt(paymentIntentId, fullReason);
      } catch (logError) {
        console.error('[StripeCheckout] Failed to log attempt:', logError);
      }
    }
    setLoading(false);
  };

  // Handle checkout cancellation - release all booths and go home
  const handleCancelCheckout = async () => {
    if (purchaseType !== 'booths' || !currentUser?.uid || !eventId || !layoutId || !Array.isArray(displayBooths)) {
      navigate('/');
      return;
    }
    
    const confirmed = window.confirm(
      'Are you sure you want to cancel checkout?\n\n' +
      'Your selected booths will be released and made available for others.'
    );
    
    if (!confirmed) return;
    
    try {
      // 1. Mark all checkedout cart items as 'removed' (this will also log the removal)
      try {
        const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
        const sessionId = sessionStorage.getItem(sessionKey);
        
        if (sessionId) {
          // Load all checkedout cart items for this session
          const checkedoutItems = await loadCartItemsByStatus({
            profileId: currentUser.uid,
            eventId,
            layoutId,
            sessionId,
            status: 'checkedout'
          });
          
          
          
          // Mark each cart item as 'removed' with proper logging
          for (const cartItem of checkedoutItems) {
            try {
              await updateCartItemAction(cartItem.cartItemId || cartItem.id, 'removed', {
                triggeredByProfileIdOverride: currentUser.uid,
                triggeredByOverride: 'checkout-cancelled'
              });
            } catch (err) {
              console.error('[StripeCheckout] Failed to mark cart item as removed:', cartItem.cartItemId || cartItem.id, err);
            }
          }
        }
      } catch (err) {
        console.error('[StripeCheckout] Error marking cart items as removed:', err);
      }
      
      // 2. Release all booths
      const vendorId = (orderData?.metadata || metadata)?.vendorId;
      const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
      const sessionId = sessionStorage.getItem(sessionKey) || null;
      if (vendorId) {
        for (const booth of displayBooths) {
          const boothId = typeof booth === 'object' && booth !== null ? booth.id : null;
          if (boothId) {
            try {
              await releaseBoothRemote({ eventId, layoutId, boothId, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Payment', type: 'Cancelled' });
          } catch (err) {
            console.error('[StripeCheckout] Failed to release booth:', boothId, err);
          }
        }
      }
      } else {
        console.warn('[StripeCheckout] No vendorId found in metadata, cannot release booths');
      }
    
    // 3. Clear checkout flag
    const checkoutKey = `checkoutStarted:${currentUser.uid}:${eventId}:${layoutId}`;
    try {
      sessionStorage.removeItem(checkoutKey);
    } catch (e) {}
    
    navigate('/');
  } catch (err) {
    console.error('[StripeCheckout] Error during checkout cancellation:', err);
    navigate('/');
  }
};  // Format booths for display - handle both objects and primitive values
  const formatBooths = (boothsArray) => {
    if (!Array.isArray(boothsArray) || boothsArray.length === 0) return '';
    return boothsArray.map(b => {
      // If booth is an object with a number property, use that
      if (typeof b === 'object' && b !== null && b.number) {
        return b.number;
      }
      // Otherwise use the value directly
      return b;
    }).join(', ');
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#1976d2',
    },
  };

  const options = {
    clientSecret,
    appearance,
    loader: 'auto',
  };

  return (
    <div className="profile-container" style={{ color: '#333' }}>
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)} disabled={timeoutExpired}>
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        
        {/* Payment Timer */}
        {purchaseType === 'booths' && !timeoutExpired && paymentTimeLeft !== null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginRight: 16,
              padding: '8px 16px',
              backgroundColor: timerPaused ? '#e8f5e9' : (paymentTimeLeft <= 60 ? '#fff3cd' : '#d1ecf1'),
              border: `1px solid ${timerPaused ? '#4caf50' : (paymentTimeLeft <= 60 ? '#ffc107' : '#bee5eb')}`,
              borderRadius: 4,
              color: timerPaused ? '#2e7d32' : (paymentTimeLeft <= 60 ? '#856404' : '#0c5460'),
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {timerPaused ? '⏸️ Timer paused (processing payment)' : '⏱️ Time to complete payment:'}
            </span>
            {!timerPaused && (
              <span style={{ 
                fontSize: '1.2em', 
                fontWeight: 800,
                color: paymentTimeLeft <= 60 ? '#d9534f' : '#0c5460'
              }}>
                {Math.floor(paymentTimeLeft / 60)}:{(paymentTimeLeft % 60).toString().padStart(2, '0')}
              </span>
            )}
          </div>
        )}
        
        <button type="button" className="cancel-btn" onClick={handleCancelCheckout} disabled={timeoutExpired}>
          Cancel Checkout
        </button>
      </div>

      <h2 className="profile-title" style={{ color: '#333' }}>Checkout</h2>

      {/* Failed Attempts Notice */}
      {attemptCount > 0 && attemptCount <= MAX_PAYMENT_ATTEMPTS && (
        <div style={{
          padding: 16,
          marginBottom: 20,
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: 4,
          color: '#856404'
        }}>
          <strong>Payment Failed</strong>
          <p style={{ marginTop: 8, marginBottom: 8 }}>
            Your timer has been reset to give you more time to complete the payment.
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Attempts remaining: {MAX_PAYMENT_ATTEMPTS - attemptCount}</strong> (after this, timer will not reset)
          </p>
        </div>
      )}

      {/* Maximum Attempts Exceeded Notice */}
      {attemptCount > MAX_PAYMENT_ATTEMPTS && (
        <div style={{
          padding: 16,
          marginBottom: 20,
          backgroundColor: '#f8d7da',
          border: '2px solid #f5c6cb',
          borderRadius: 4,
          color: '#721c24'
        }}>
          <strong>⚠️ Maximum Payment Attempts Reached</strong>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            You have used all {MAX_PAYMENT_ATTEMPTS} payment attempts. The timer will no longer reset on failures. 
            Please ensure your payment information is correct before trying again.
          </p>
        </div>
      )}

      {/* Timeout Expired Warning */}
      {timeoutExpired && (
        <div style={{
          padding: 20,
          marginBottom: 20,
          backgroundColor: '#f8d7da',
          border: '2px solid #f5c6cb',
          borderRadius: 4,
          color: '#721c24'
        }}>
          <h3 style={{ marginTop: 0, color: '#721c24' }}>⚠️ Payment Time Expired</h3>
          <p style={{ marginBottom: 12 }}>
            The time allowed to complete your payment has expired. Your selected booths have been released and are now available for others to purchase.
          </p>
          <p style={{ marginBottom: 16 }}>
            Please return to booth selection and select your booths again to start a new payment process.
          </p>
          <button 
            onClick={() => navigate(-1)} 
            className="back-btn"
            style={{ backgroundColor: '#1976d2', color: '#fff' }}
          >
            Return to Booth Selection
          </button>
        </div>
      )}

      {loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          <p>Initializing payment...</p>
        </div>
      )}

      {error && (
        <div className="profile-error" style={{ marginBottom: 16 }}>
          {error}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate(-1)} className="back-btn">
              Go Back
            </button>
          </div>
        </div>
      )}

      {!loading && !error && !timeoutExpired && (
        <>
          {/* Order Summary */}
          <div style={{ 
            marginBottom: 24, 
            padding: 16, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 4,
            color: '#333'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#333' }}>Order Summary</h3>
            
            {displayEventName && (
              <div style={{ marginBottom: 8 }}>
                <strong>Event:</strong> {displayEventName}
              </div>
            )}
            
            {displayBooths && displayBooths.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <strong>Booths:</strong> {formatBooths(displayBooths)}
              </div>
            )}
            
            <div style={{ marginBottom: 8 }}>
              <strong>Description:</strong> {displayDescription}
            </div>
            
            <div style={{ 
              marginTop: 16, 
              paddingTop: 16, 
              borderTop: '2px solid #dee2e6',
              fontSize: '1.2em',
              fontWeight: 'bold'
            }}>
              Total: {(() => {
                return formatUsdFromCents(displayAmount);
              })()}
              {/* Only show refund info if a refund is actually being requested (add your own logic here) */}
              {/* Example: showRefundInfo could be a state or prop set when refund is in progress */}
              {/* {showRefundInfo && eventData && (eventData.refundAmount || eventData.refundPercentage) && (
                <div style={{ fontSize: '0.95em', fontWeight: 'normal', marginTop: 8, color: '#555' }}>
                  {eventData.refundAmount ? (
                    <div>
                      Refund Amount: <strong>${(Number(eventData.refundAmount) / 10).toFixed(2)}</strong>
                    </div>
                  ) : null}
                  {eventData.refundPercentage ? (
                    <div>
                      Refund Percentage: <strong>{eventData.refundPercentage}%</strong>
                    </div>
                  ) : null}
                </div>
              )} */}
            </div>
          </div>

          {/* Payment Form */}
          <div style={{ 
            padding: 20, 
            backgroundColor: '#fff', 
            border: '1px solid #dee2e6',
            borderRadius: 4 
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, color: '#333' }}>Payment Details</h3>
            
            {clientSecret && stripePromise && (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm
                  paymentIntentId={paymentIntentId}
                  amount={displayAmount}
                  customerEmail={customerEmail}
                  customerPhone={customerPhone}
                  customerZip={customerZip}
                  customerCountry={customerCountry}
                  customerState={customerState}
                  customerCity={customerCity}
                  customerAddressLine={customerAddressLine}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  onCancel={handleCancelCheckout}
                  onPaymentStart={handlePaymentStart}
                  onPaymentComplete={handlePaymentComplete}
                  policyAcknowledged={policyAcknowledged}
                />
              </Elements>
            )}
          </div>

          {/* Acknowledgment Checkbox - placed after payment form */}
          <div style={{ 
            marginTop: 24,
            padding: 20, 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6',
            borderRadius: 4 
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              gap: 12,
              cursor: 'pointer',
              fontSize: '1em'
            }}>
              <input
                type="checkbox"
                checked={policyAcknowledged}
                onChange={(e) => setPolicyAcknowledged(e.target.checked)}
                style={{ 
                  marginTop: 4,
                  width: 18,
                  height: 18,
                  cursor: 'pointer'
                }}
              />
              <span style={{ flex: 1, color: '#333' }}>
                <strong>I acknowledge that I have read and agree to the policies, terms, and conditions.</strong>
              </span>
            </label>
          </div>

          {/* Policies and Terms - placed after acknowledgment */}
          {eventData && (eventData.cancellationPolicy || eventData.termsAndConditions || eventData.policyLink) && (
            <div style={{ 
              marginTop: 24,
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6',
              borderRadius: 4 
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, color: '#333' }}>Event Policies & Terms</h3>
              
              {eventData.cancellationPolicy && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 8, color: '#555', fontSize: '1em' }}>Cancellation Policy:</h4>
                  <div style={{ 
                    padding: 12, 
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    fontSize: '0.95em',
                    color: '#333',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {eventData.cancellationPolicy}
                  </div>
                </div>
              )}
              
              {eventData.termsAndConditions && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 8, color: '#555', fontSize: '1em' }}>Terms and Conditions:</h4>
                  <div style={{ 
                    padding: 12, 
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    fontSize: '0.95em',
                    color: '#333',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}>
                    {eventData.termsAndConditions}
                  </div>
                </div>
              )}
              
              {eventData.policyLink && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 8, color: '#555', fontSize: '1em' }}>Additional Policies:</h4>
                  <a 
                    href={eventData.policyLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#1976d2',
                      textDecoration: 'underline'
                    }}
                  >
                    {eventData.policyLink}
                  </a>
                </div>
              )}
            </div>
          )}

          {/*
          <div style={{ 
            marginTop: 24, 
            padding: 16, 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107',
            borderRadius: 4, 
            color: '#856404'
          }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Test Mode - Use these test cards:</strong>
            </div>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Success:</strong> <code style={{ 
                  backgroundColor: '#fff', 
                  padding: '2px 6px', 
                  borderRadius: 3,
                  color: '#333'
                }}>4242 4242 4242 4242</code>
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Decline:</strong> <code style={{ 
                  backgroundColor: '#fff', 
                  padding: '2px 6px', 
                  borderRadius: 3,
                  color: '#333'
                }}>4000 0000 0000 0002</code>
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Requires authentication:</strong> <code style={{ 
                  backgroundColor: '#fff', 
                  padding: '2px 6px', 
                  borderRadius: 3,
                  color: '#333'
                }}>4000 0025 0000 3155</code>
              </li>
            </ul>
            <div style={{ fontSize: '0.9em', marginTop: 8 }}>
              Use any future expiry date (e.g., 12/34) and any 3-digit CVC.
            </div>
          </div>
          */}
        </>
      )}
    </div>
  );
};

export default StripeCheckout;
